import { motion } from "framer-motion"
import { Bot, ChevronRight, Workflow } from "lucide-react"
import { Link } from "react-router-dom"
import { BotStatusBadge, type CustomBot } from "@/entities/bot-builder"
import type { BotReach } from "@/entities/bot-stats"
import { formatVersion } from "@/shared/lib/format-version"
import { fadeUp } from "@/shared/lib/motion"
import { Skeleton } from "@/shared/ui/skeleton"

type Reach = BotReach["bots"][number]

const number = new Intl.NumberFormat("ru-RU")

/** Бот в списке: имя, состояние, сценарий и сколько людей он может получить рассылку. */
export function BotRow({
	bot,
	reach,
	animate = true,
}: {
	bot: CustomBot
	reach?: Reach
	/** Каскад только на первом показе списка: дальше смену ведёт переход всего блока. */
	animate?: boolean
}) {
	return (
		<motion.li variants={animate ? fadeUp : undefined}>
			<Link
				to={`/admin/bots/${bot.id}`}
				className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-foreground/20"
			>
				<span className="grid size-11 shrink-0 place-items-center rounded-xl bg-secondary text-foreground/80">
					<Bot className="size-5" />
				</span>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<span className="truncate font-medium">{bot.name}</span>
						<BotStatusBadge status={bot.status} />
					</div>
					<p className="mt-0.5 truncate text-sm text-muted-foreground">
						@{bot.username}
						{reach ? (
							<>
								{" · "}
								<span className="tabular-nums">{number.format(reach.reachable)}</span> получат
								рассылку
								{reach.blocked > 0 ? (
									<>
										{", "}
										<span className="tabular-nums">{number.format(reach.blocked)}</span>{" "}
										заблокировали
									</>
								) : null}
								{reach.awaitingId > 0 ? (
									<>
										{", "}
										<span className="tabular-nums">{number.format(reach.awaitingId)}</span> ждут
										поиска ID
									</>
								) : null}
							</>
						) : null}
					</p>
					{bot.status === "ERROR" && bot.lastError ? (
						<p className="mt-1 line-clamp-2 text-xs text-red-400">{bot.lastError}</p>
					) : null}
				</div>
				<div className="hidden min-w-0 max-w-64 items-center gap-2 text-sm text-muted-foreground sm:flex">
					<Workflow className="size-4 shrink-0" />
					<span className="truncate">
						{bot.scenario
							? `${bot.scenario.name}, ${formatVersion(bot.scenario.latestVersion)}`
							: "Сценарий не выбран"}
					</span>
				</div>
				<ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
			</Link>
		</motion.li>
	)
}

/** Заглушка строки бота: иконка, имя со статусом, @username и сценарий справа. */
export function BotRowSkeleton() {
	return (
		<li
			aria-hidden="true"
			className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4"
		>
			<Skeleton className="size-11 shrink-0 rounded-xl" />
			<div className="min-w-0 flex-1">
				<div className="flex h-6 items-center gap-2">
					<Skeleton className="h-4 w-40 rounded" />
					<Skeleton className="h-[22px] w-20 rounded-full" />
				</div>
				<div className="mt-0.5 flex h-5 items-center">
					<Skeleton className="h-3.5 w-72 max-w-full rounded" />
				</div>
			</div>
			<Skeleton className="hidden h-4 w-48 rounded sm:block" />
			<Skeleton className="size-4 shrink-0 rounded" />
		</li>
	)
}
